import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import HeaderComponent from '../components/General/HeaderComponent';
import MenuBarComponent from '../components/Panel/MenuBarComponent';
import { 
  FiFolder, 
  FiFile, 
  FiUpload, 
  FiDownload, 
  FiTrash2, 
  FiPlus,
  FiSearch,
  FiX,
  FiAlertTriangle
} from 'react-icons/fi';
import { 
  getProjectFiles, 
  uploadFile, 
  createFolder, 
  deleteFile, 
  downloadFile, 
  searchFiles,
  formatFileSize,
  getFileTypeColor,
  isFileTypeSupported,
  MAX_FILE_SIZE
} from '../services/files';

const Files = () => {
  const { id } = useParams();
  const [projectData, setProjectData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [files, setFiles] = useState([]);
  const [currentPath, setCurrentPath] = useState('/');
  const [pathHistory, setPathHistory] = useState(['/']);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [currentFolderId, setCurrentFolderId] = useState(null);

  // Dosyaları yükle
  const fetchFiles = useCallback(async (folderId = null) => {
    if (!id) return;
    
    setLoading(true);
    setError('');
    
    try {
      const response = await getProjectFiles(id, folderId);
      setFiles(response.files);
      setCurrentFolderId(folderId);
    } catch (err) {
      setError('Dosyalar yüklenirken bir hata oluştu.');
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Arama yap
  const performSearch = useCallback(async (query) => {
    if (!id) return;
    
    setError('');
    try {
      if (query.trim()) {
        setLoading(true);
        const response = await searchFiles(id, query);
        setFiles(response.files);
        setLoading(false);
      } else {
        // Arama boşsa mevcut klasörü yükle (loading gösterme)
        const response = await getProjectFiles(id, currentFolderId);
        setFiles(response.files);
        setCurrentFolderId(currentFolderId);
      }
    } catch (err) {
      setError('Arama yapılırken bir hata oluştu.');
      setFiles([]);
      setLoading(false);
    }
  }, [id, currentFolderId]);

  useEffect(() => {
    if (id) {
      setProjectData({
        pname: `Proje ${id}`,
        pdesc: 'Dosya yönetimi sayfası',
        created_at: new Date().toISOString(),
        userstatus: 11
      });
      fetchFiles();
    }
  }, [id, fetchFiles]);

  // Arama değiştiğinde
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      performSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, performSearch]);

  const getFileIcon = (type, name) => {
    if (type === 'folder') {
      return <FiFolder className="text-blue-500" size={24} />;
    }
    
    const colorClass = getFileTypeColor(type, name);
    return <FiFile className={colorClass} size={24} />;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR');
  };

  // Klasöre gir
  const enterFolder = (folderId, folderName) => {
    setPathHistory(prev => [...prev, folderName]);
    setCurrentPath(folderName);
    setSelectedFiles([]);
    setSearchQuery(''); // Arama temizle
    fetchFiles(folderId);
  };

  // Üst klasöre çık
  const goBack = () => {
    if (pathHistory.length > 1) {
      const newHistory = [...pathHistory];
      newHistory.pop();
      setPathHistory(newHistory);
      setCurrentPath(newHistory[newHistory.length - 1]);
      setSelectedFiles([]);
      setSearchQuery(''); // Arama temizle
      
      // Parent folder ID'yi bul ve dosyaları yükle
      const parentFolderId = newHistory.length === 1 ? null : null; // Bu kısım API'den gelecek
      fetchFiles(parentFolderId);
    }
  };

  // Ana klasöre dön
  const goToRoot = () => {
    setPathHistory(['/']);
    setCurrentPath('/');
    setSelectedFiles([]);
    setSearchQuery(''); // Arama temizle
    fetchFiles(null);
  };

  // Breadcrumb path'i oluştur
  const getBreadcrumbPath = () => {
    return currentPath.split('/').filter(segment => segment !== '');
  };

  const handleFileSelect = (fileId) => {
    setSelectedFiles(prev => 
      prev.includes(fileId) 
        ? prev.filter(id => id !== fileId)
        : [...prev, fileId]
    );
  };

  const handleSelectAll = () => {
    if (selectedFiles.length === files.length) {
      setSelectedFiles([]);
    } else {
      setSelectedFiles(files.map(file => file.id));
    }
  };

  const handleDelete = async (fileId) => {
    if (window.confirm('Bu dosyayı silmek istediğinizden emin misiniz?')) {
      try {
        await deleteFile(fileId);
        setFiles(prev => prev.filter(file => file.id !== fileId));
        setSelectedFiles(prev => prev.filter(id => id !== fileId));
      } catch (err) {
        setError('Dosya silinirken bir hata oluştu.');
      }
    }
  };

  const handleDownload = async (fileId) => {
    try {
      const response = await downloadFile(fileId);
      const link = document.createElement('a');
      link.href = response.download_url;
      link.download = response.file_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setError('Dosya indirilirken bir hata oluştu.');
    }
  };

  const handleUpload = () => {
    setShowUploadModal(true);
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    
    try {
      const response = await createFolder(id, {
        name: newFolderName,
        parentFolderId: currentFolderId
      });
      setFiles(prev => [...prev, response.folder]);
      setNewFolderName('');
      setShowCreateFolderModal(false);
    } catch (err) {
      setError('Klasör oluşturulurken bir hata oluştu.');
    }
  };

  // Sürükle-bırak işlemleri
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragIn = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setDragActive(true);
    }
  };

  const handleDragOut = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      await handleFileUpload(e.dataTransfer.files);
    }
  };

  const handleFileUpload = async (fileList) => {
    setUploading(true);
    setError('');

    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];
      
      // Dosya türü kontrolü
      if (!isFileTypeSupported(file.name)) {
        setError(`${file.name} desteklenmeyen dosya türü.`);
        continue;
      }

      // Dosya boyutu kontrolü
      if (file.size > MAX_FILE_SIZE) {
        setError(`${file.name} çok büyük. Maksimum 50MB olmalı.`);
        continue;
      }

      try {
        const response = await uploadFile(id, file, currentFolderId);
        setFiles(prev => [...prev, response.file]);
      } catch (err) {
        setError(`${file.name} yüklenirken hata oluştu.`);
      }
    }

    setUploading(false);
    setShowUploadModal(false);
  };

  if (loading) {
    return (
      <div className="w-full dark:bg-dark-100 bg-stone-100 min-h-screen">
        <div className="container mx-auto">
          <HeaderComponent title={projectData?.pname || `Proje ${id}`} />
          <MenuBarComponent title="files" />
          <div className="flex justify-center items-center h-96">
            <div className="text-center space-y-6">
              {/* Modern Loading Animation */}
              <div className="relative mx-auto w-20 h-20">
                {/* Outer Ring */}
                <div className="absolute inset-0 rounded-full border-4 border-gray-200 dark:border-gray-700"></div>
                {/* Animated Ring */}
                <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-red-600 animate-spin"></div>
                {/* Inner Pulse */}
                <div className="absolute inset-2 rounded-full bg-red-50 dark:bg-red-900/20 animate-pulse"></div>
                {/* Center Dot */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-2 h-2 bg-red-600 rounded-full animate-ping"></div>
                </div>
              </div>
              
              {/* Loading Text */}
              <div className="space-y-2">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Dosyalar Yükleniyor
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Lütfen bekleyin...
                </p>
              </div>
              
              {/* Progress Dots */}
              <div className="flex justify-center space-x-1">
                <div className="w-2 h-2 bg-red-600 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
                <div className="w-2 h-2 bg-red-600 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
                <div className="w-2 h-2 bg-red-600 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full dark:bg-dark-100 bg-stone-100 min-h-screen">
      <div className="container mx-auto">
        <HeaderComponent title={projectData?.pname || `Proje ${id}`} />
        <MenuBarComponent title="files" />
        
        {/* Error Message */}
        {error && (
          <div className="mt-2 mx-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <div className="flex items-center">
              <FiAlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
              <span className="text-red-800 dark:text-red-200">{error}</span>
              <button
                onClick={() => setError('')}
                className="ml-auto text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
              >
                <FiX size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Dosya Yönetimi İçeriği */}
        <div 
          className={`mt-2 bg-white dark:bg-dark-300 rounded-xl shadow-sm border border-gray-200 dark:border-dark-300 mb-6 h-[calc(100vh-200px)] overflow-hidden ${
            dragActive ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : ''
          }`}
          onDragEnter={handleDragIn}
          onDragLeave={handleDragOut}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          {/* Breadcrumb Navigasyon */}
          <div className="h-12 bg-gray-100 dark:bg-dark-300 border-b border-gray-200 dark:border-gray-600 flex items-center px-6">
            <div className="flex items-center space-x-2">
              <button
                onClick={goToRoot}
                className="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 text-sm font-medium"
              >
                Ana Klasör
              </button>
              {getBreadcrumbPath().map((segment, index) => (
                <React.Fragment key={index}>
                  <span className="text-gray-400 dark:text-gray-500">/</span>
                  <button
                    onClick={() => {
                      const path = '/' + getBreadcrumbPath().slice(0, index + 1).join('/');
                      setCurrentPath(path);
                      setPathHistory(prev => [...prev.slice(0, index + 1), path]);
                      setSelectedFiles([]);
                    }}
                    className="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 text-sm font-medium"
                  >
                    {segment}
                  </button>
                </React.Fragment>
              ))}
            </div>
            
            <div className="flex items-center space-x-2 ml-auto">
              <button
                onClick={goBack}
                disabled={pathHistory.length <= 1}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                  pathHistory.length <= 1
                    ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
                    : 'text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                }`}
              >
                ← Geri
              </button>
            </div>
          </div>

          {/* Üst Araç Çubuğu */}
          <div className="h-16 bg-gray-50 dark:bg-dark-300 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between px-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleUpload}
                className="flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 dark:bg-[#797BF2] dark:hover:bg-[#6B73E8] text-white text-sm rounded-md transition-colors"
              >
                <FiUpload className="mr-2" size={16} />
                Dosya Yükle
              </button>
              <button 
                onClick={() => setShowCreateFolderModal(true)}
                className="flex items-center px-4 py-2 bg-red-500 hover:bg-red-600 dark:bg-[#9CA3F0] dark:hover:bg-[#8B94ED] text-white text-sm rounded-md transition-colors"
              >
                <FiPlus className="mr-2" size={16} />
                Klasör Oluştur
              </button>
              {selectedFiles.length > 0 && (
                <button
                  onClick={() => handleDelete(selectedFiles[0])}
                  className="flex items-center px-4 py-2 bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700 text-white text-sm rounded-md transition-colors"
                >
                  <FiTrash2 className="mr-2" size={16} />
                  Seçilenleri Sil ({selectedFiles.length})
                </button>
              )}
            </div>

            <div className="flex items-center space-x-4">
              {/* Arama */}
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={16} />
                <input
                  type="text"
                  placeholder="Dosya ara..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 dark:bg-dark-300 dark:text-white dark:placeholder-gray-400 w-64"
                />
              </div>

            </div>
          </div>

          {/* Dosya Listesi */}
          <div className="h-[calc(100%-7rem)] overflow-y-auto p-6 bg-white dark:bg-dark-300">
            {dragActive && (
              <div className="absolute inset-0 bg-blue-500/10 dark:bg-blue-500/20 flex items-center justify-center z-10">
                <div className="text-center">
                  <FiUpload className="w-16 h-16 text-blue-500 mx-auto mb-4" />
                  <p className="text-lg font-medium text-blue-600 dark:text-blue-400">Dosyaları buraya bırakın</p>
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              {/* Başlık Satırı */}
              <div className="flex items-center p-3 bg-gray-50 dark:bg-dark-300 rounded-lg">
                <div className="flex items-center w-8">
                  <input
                    type="checkbox"
                    checked={selectedFiles.length === files.length && files.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-dark-300 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
                  />
                </div>
                <div className="flex-1 font-medium text-gray-700 dark:text-gray-300">Ad</div>
                <div className="w-24 font-medium text-gray-700 dark:text-gray-300">Boyut</div>
                <div className="w-32 font-medium text-gray-700 dark:text-gray-300">Değiştirilme</div>
                <div className="w-16 font-medium text-gray-700 dark:text-gray-300">İşlemler</div>
              </div>

              {/* Dosya Satırları */}
              {files.map((file) => (
                <div
                  key={file.id}
                  className={`flex items-center p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-300 cursor-pointer ${
                    selectedFiles.includes(file.id) 
                      ? 'bg-blue-50 dark:bg-blue-900/20' 
                      : 'bg-white dark:bg-dark-300'
                  }`}
                  onClick={() => {
                    if (file.isFolder) {
                      enterFolder(file.id, file.originalName);
                    } else {
                      handleFileSelect(file.id);
                    }
                  }}
                >
                  <div className="flex items-center w-8">
                    <input
                      type="checkbox"
                      checked={selectedFiles.includes(file.id)}
                      onChange={() => handleFileSelect(file.id)}
                      className="rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-dark-300 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400"
                    />
                  </div>
                  <div className="flex items-center flex-1">
                    <div className="mr-3">
                      {getFileIcon(file.fileType, file.originalName)}
                    </div>
                    <span className="text-gray-900 dark:text-white font-medium">
                      {file.originalName}
                    </span>
                  </div>
                  <div className="w-24 text-gray-500 dark:text-gray-400 text-sm">
                    {file.size}
                  </div>
                  <div className="w-32 text-gray-500 dark:text-gray-400 text-sm">
                    {formatDate(file.modified)}
                  </div>
                  <div className="w-16 flex space-x-2">
                    {!file.isFolder && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload(file.id);
                        }}
                        className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
                        title="İndir"
                      >
                        <FiDownload size={16} className="text-gray-600 dark:text-gray-300" />
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(file.id);
                      }}
                      className="p-1 hover:bg-red-100 dark:hover:bg-red-900/20 rounded"
                      title="Sil"
                    >
                      <FiTrash2 size={16} className="text-red-500" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {files.length === 0 && !loading && (
              <div className="flex flex-col items-center justify-center h-64 text-gray-500 dark:text-gray-400">
                <FiFolder size={48} className="mb-4" />
                <p className="text-lg font-medium">
                  {searchQuery ? 'Arama sonucu bulunamadı' : 'Dosya bulunamadı'}
                </p>
                <p className="text-sm">
                  {searchQuery ? 'Arama kriterlerinizi değiştirmeyi deneyin' : 'İlk dosyanızı yükleyerek başlayın'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Klasör Oluşturma Modal */}
        {showCreateFolderModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-dark-300 rounded-lg p-6 w-96">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Yeni Klasör Oluştur
              </h3>
              <input
                type="text"
                placeholder="Klasör adı"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-dark-300 dark:text-white dark:placeholder-gray-400"
                onKeyPress={(e) => e.key === 'Enter' && handleCreateFolder()}
              />
              <div className="flex justify-end space-x-3 mt-4">
                <button
                  onClick={() => {
                    setShowCreateFolderModal(false);
                    setNewFolderName('');
                  }}
                  className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                >
                  İptal
                </button>
                <button
                  onClick={handleCreateFolder}
                  disabled={!newFolderName.trim()}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-md"
                >
                  Oluştur
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Dosya Yükleme Modal */}
        {showUploadModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-dark-300 rounded-lg p-6 w-96">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Dosya Yükle
              </h3>
              <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center">
                <FiUpload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Dosyaları buraya sürükleyin veya seçin
                </p>
                <input
                  type="file"
                  multiple
                  onChange={(e) => handleFileUpload(e.target.files)}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md cursor-pointer"
                >
                  Dosya Seç
                </label>
              </div>
              <div className="flex justify-end mt-4">
                <button
                  onClick={() => setShowUploadModal(false)}
                  className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                >
                  İptal
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Uploading Overlay */}
        {uploading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-dark-300 rounded-lg p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto mb-4"></div>
              <p className="text-gray-900 dark:text-white">Dosyalar yükleniyor...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Files;
